import { navigate } from '@reach/router';
import { Button } from 'components/button/Button';
import { ProgressIndicator } from 'components/progressIndicator/progressIndicator';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ViewApplicationStagesProps } from './models';
import {
  WorkflowStage,
  WorkflowStageItem,
  WorkflowTemplate,
} from './ViewApplication';
import BigInformationLogo from 'assets/apply.clevry/Icon_Guide.png';
import BigInformationLogoYellow from 'assets/apply.clevry/Icon_Guide_Yellow.png';
import BigInformationLogoWhite from 'assets/apply.clevry/Icon_Guide_White.png';
import BigInformationLogoBlack from 'assets/apply.clevry/Icon_Guide_Black.png';
import InformationLogo from 'assets/apply.clevry/Icon_Label_MoreInfo.png';
import CalculatorLogo from 'assets/apply.clevry/Icon_Guide_01.png';
import WatchLogo from 'assets/apply.clevry/Icon_Guide_02.png';
import PaperLogo from 'assets/apply.clevry/Icon_Guide_03.png';
import MobileLogo from 'assets/apply.clevry/Icon_Guide_04.png';
import DesktopLogo from 'assets/apply.clevry/Icon_Guide_05.png';
import GreenTickLogo from 'assets/apply.clevry/Icon_Tag_01.png';
import BigGreenTickLogo from 'assets/apply.clevry/Icon_Valid.png';
import { openModal } from 'reducers/modal';
import { ApplicationSummaryType } from 'components/applicationSummary/models';
import { setIFramedProng } from 'reducers/iframedProng';
import { transpileModule } from 'typescript';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';
import { translations } from 'translations';
import { ViewApplicationStagesDetailsFooter } from './ViewApplicationStagesDetailsFooter';
import { format } from 'date-fns';
import { cancelBooking, resendApplicationScores } from './ViewApplicationApi';
import { Toast } from 'components/toastMessages/toast';
import {
  selectProjectId,
  selectSignUpProjectId,
  setAssessmentId,
  setProjectId,
} from 'reducers/virtualAssessments';
import { selectWebskin, signOut } from 'reducers/auth';
import { Tooltip } from 'components/helpers/Tooltip';
import { AppPath } from 'appConstants';
import { clarity } from 'clarity-js';
import instance from 'api';
import { endpoints } from 'config';
import {
  selectPublicBrowseOpportunitiesSignIn,
  setPublicBrowseOpportunitiesSignIn,
} from 'reducers/browseOpportunities';
import useAvailableLanguagesByProngId from 'api/getAvailableLanguagesByProngId/getAvailableLanguagesByProngId';
import AssestmentCard from './AssestmentCard/AssestmentCard';
import { RedirectCustomPage } from 'components/pages/virtualAssessments/RedirectCustomPage';
import AccessibilitySwitcher from "helpers/accessibilitySwitcher";
import { selectAccessibilityData } from 'reducers/accessibility';

export const completeStage = async (jobApplicationId: number) => {
  try {
    const res = await instance.post(endpoints.jobApplication.completeStage, {
      jobApplicationId: jobApplicationId,
    });
    if (res.data.status === '1') {
      return {
        response: res.data,
        errors: null,
      };
    } else {
      return {
        response: null,
        errors: res.data.errors,
      };
    }
  } catch (res) {
    const error: any = res;

    if (error.response && error.response.data && error.response.data.errors) {
      return {
        response: null,
        errors: error.response.data.errors,
      };
    } else {
      return {
        response: null,
        errors: [],
      };
    }
  }
};

enum ViewTypes {
  DEFAULT = 'DEFAULT',
  RESEND_SCORES = 'RESEND_SCORES',
  SCORES_SENT = 'SCORES_SENT',
  REDIRECT_PAGE = 'REDIRECT_PAGE',
}

const ViewApplicationStages: React.FC<ViewApplicationStagesProps> = ({
  jobApplicationId,
  progress,
  title,
  stages,
  applicationType,
  assessmentId,
  locationProp,
  projectId,
  projectIntroVideoURL,
  clientName,
  isAssessmentOnly,
  campaignWelcomeText,
  projectLogoURL,
  redirectURL,
  redirectText,
  supportEmail,
  applicationReference,
  resendScores,
  scoresHaveBeenSent,
  testsAllowMultipleLanguages = false,
  isAllowReasonableAdjustment
}) => {
  const [viewSelected, setViewSelected] = useState<ViewTypes>(
    ViewTypes.DEFAULT
  );
  const dispatch = useDispatch();

  const signUpProjectId = useSelector(selectSignUpProjectId);
  const webSkinDetails = useSelector(selectWebskin); // To access whether the bottom text should be displayed or not
  const accessibilityData = useSelector(selectAccessibilityData);

  //TODO: fix non-null assertion
  const { data: prongLanguageData } = useAvailableLanguagesByProngId(stages!, testsAllowMultipleLanguages)
  const [clarityProjectId, setClarityProjectId] = useState<number | null>(null);

  const { t } = useTranslation(
    ['View+Application+Stages', 'View+Application+Stages+Details+Footer'],
    { i18n }
  );

  const currentWebskin = window.sessionStorage.getItem('webskin')
    ? window.sessionStorage.getItem('webskin')
    : 'defaultTheme';

  const getWindowDimensions = () => {
    const { innerWidth: width, innerHeight: height } = window;
    return {
      width,
      height,
    };
  };

  // Clear redux value if signing in for a project/campaign they've already applied for so don't reach the JobDescription page
  const publicBrowseOpportunitiesSignIn = useSelector(
    selectPublicBrowseOpportunitiesSignIn
  );

  useEffect(() => {
    if (publicBrowseOpportunitiesSignIn) {
      dispatch(setPublicBrowseOpportunitiesSignIn(false));
    }
  }, [publicBrowseOpportunitiesSignIn]);

  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions()
  );

  useEffect(() => {
    if (redirectURL && redirectText) {
      setViewSelected(ViewTypes.REDIRECT_PAGE);
    } else if (scoresHaveBeenSent) {
      setViewSelected(ViewTypes.SCORES_SENT);
    } else if (resendScores) {
      setViewSelected(ViewTypes.RESEND_SCORES);
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions(getWindowDimensions());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (projectIntroVideoURL && progress === 0) {
      dispatch(
        openModal({
          modalType: 'PROJECT_INTRO_VIDEO',
          data: {
            title: 'Introduction',
            jobApplicationId: jobApplicationId,
            projectIntroVideoURL: projectIntroVideoURL,
            campaignWelcomeText: campaignWelcomeText,
          },
        })
      );
    }
  }, [projectIntroVideoURL]);

  useEffect(() => {
    if ((projectId || signUpProjectId) && !clarityProjectId) {
      let finalProjectId = projectId ?? signUpProjectId;

      setClarityProjectId(finalProjectId);
    }
  }, [projectId, signUpProjectId]);

  // useEffect(() => {
  //   console.log('### 2 clarityProjectId: ', clarityProjectId);

  //   if (clarityProjectId) {
  //     const clarityInstance = (window as any)['clarity'] as any;
  //     console.log('### 2 clarityInstance: ', clarityInstance);
  //     if (clarityInstance) {
  //       console.log('### 2 clarityInstance PASSED: ');
  //       clarityInstance('set', 'ProjectID', clarityProjectId.toString());
  //     }
  //   }
  // }, [clarityProjectId]);

  useEffect(() => {
    const checkClarity = () => {
      const clarityInstance = (window as any)['clarity'] as any;
      if (clarityInstance && clarityProjectId) {
        clarityInstance('set', 'ProjectID', clarityProjectId.toString());
      } else {
        setTimeout(checkClarity, 1000); // Retry after 1 second
      }
    };

    if (clarityProjectId) {
      checkClarity();
    }
  }, [clarityProjectId]);

  const handleResendScores = async (
    projectId?: number,
    applicationReference?: string | number
  ) => {
    if (!projectId) {
      Toast.openToastErrorMessage(
        t('SSCandidate_View_Application_Stages_Something_Went_Wrong'),
        { className: 'ActivityNotificationToast' }
      );
      return;
    }

    const result = await resendApplicationScores({
      projectId: projectId,
      applicationReference: applicationReference,
    });
    if (result.response) {
      if (redirectURL) {
        navigate(redirectURL);
      } else {
        setViewSelected(ViewTypes.SCORES_SENT);
      }
    } else {
      Toast.openToastErrorMessage(
        t('SSCandidate_View_Application_Stages_Something_Went_Wrong'),
        { className: 'ActivityNotificationToast' }
      );
    }
  };

  const handleStart = (
    template: WorkflowTemplate,
    isLastAssessmentInStage: boolean = false,
    selectedLanguage?: string
  ) => {
    if (assessmentId) {
      dispatch(setAssessmentId(parseInt(assessmentId)));
    }
    let redirectUrlProps = {};
    let languageProps = {}

    if (selectedLanguage) {
      languageProps = { selectedLanguage }
    }


    if (locationProp && locationProp.state && locationProp.state.projectId) {
      const projectId =
        typeof locationProp.state.projectId === 'string'
          ? Number(locationProp.state.projectId)
          : locationProp.state.projectId;

      redirectUrlProps = {
        projectId,
      };
      dispatch(setProjectId(parseInt(projectId)));
    }

    if (template.prongCode === 'ApplicationForm') {
      if (
        redirectUrlProps &&
        typeof redirectUrlProps === 'object' &&
        Object.keys(redirectUrlProps).length > 0
      ) {
        navigate('/job-application', {
          state: {
            assessmentProngID: template.assessmentProngID,
            redirectUrl: `/view-application/${assessmentId}/${applicationType}/stages`,
            redirectUrlProps: redirectUrlProps,
            isStageApplicationForm: true,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
          },
        });
      } else {
        navigate('/job-application', {
          state: {
            assessmentProngID: template.assessmentProngID,
            isStageApplicationForm: true,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
          },
        });
      }

      return;
    }

    if (template.prongCode === 'VideoInterview') {
      if (
        redirectUrlProps &&
        typeof redirectUrlProps === 'object' &&
        Object.keys(redirectUrlProps).length > 0
      ) {
        navigate('/recorded-interview', {
          state: {
            assessmentProngID: template.assessmentProngID,
            redirectUrl: `/view-application/${assessmentId}/${applicationType}/stages`,
            redirectUrlProps: redirectUrlProps,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
            ...languageProps,
          },
        });
      } else {
        navigate('/recorded-interview', {
          state: {
            assessmentProngID: template.assessmentProngID,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
            ...languageProps,
          },
        });
      }

      return;
    }

    if (template.prongCode === 'RecordedPresentation') {
      if (
        redirectUrlProps &&
        typeof redirectUrlProps === 'object' &&
        Object.keys(redirectUrlProps).length > 0
      ) {
        navigate('/presentation-activity', {
          state: {
            assessmentProngID: template.assessmentProngID,
            redirectUrl: `/view-application/${assessmentId}/${applicationType}/stages`,
            redirectUrlProps: redirectUrlProps,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
            ...languageProps,
          },
        });
      } else {
        navigate('/presentation-activity', {
          state: {
            assessmentProngID: template.assessmentProngID,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
            ...languageProps,
          },
        });
      }

      return;
    }
    if (template.prongCode === 'BusinessCase') {
      if (
        redirectUrlProps &&
        typeof redirectUrlProps === 'object' &&
        Object.keys(redirectUrlProps).length > 0
      ) {
        navigate('/business-case-activity', {
          state: {
            assessmentProngID: template.assessmentProngID,
            redirectUrl: `/view-application/${assessmentId}/${applicationType}/stages`,
            redirectUrlProps: redirectUrlProps,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
            ...languageProps,
          },
        });
      } else {
        navigate('/business-case-activity', {
          state: {
            assessmentProngID: template.assessmentProngID,
            isLastAssessmentInStage: isLastAssessmentInStage,
            jobApplicationId: jobApplicationId,
            ...languageProps,
          },
        });
      }

      return;
    }
    if (template.isBookable === true && template.hasBooking === false) {
      navigate('/live-applications/bookings', {
        state: {
          TemplateId: template.templateID,
          ProjectId: projectId,
          assessmentProngID: template.assessmentProngID,
          projectName: template.templateName,
          isLastAssessmentInStage: isLastAssessmentInStage,
          jobApplicationId: jobApplicationId,
          ...languageProps,
        },
      });
      return;
    }
    if (template.hasBooking && template.prongCode === 'LiveInterview') {
      navigate('/live-interview', {
        state: {
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          isLastAssessmentInStage: isLastAssessmentInStage,
          ...languageProps,
        },
      });
      return;
    }
    if (template.hasBooking && template.prongCode === 'VirtualMeeting') {
      navigate('/virtual-meeting', {
        state: {
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          isLastAssessmentInStage: isLastAssessmentInStage,
          ...languageProps,
        },
      });
      return;
    }
    if (template.hasBooking && template.prongCode === 'VideoGroupExercise') {
      navigate('/virtual-team-discussion-activity', {
        state: {
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          isLastAssessmentInStage: isLastAssessmentInStage,
          ...languageProps,
        },
      });
      return;
    }

    if (template.prongCode === 'PQ') {
      navigate('/virtual-assessments', {
        state: {
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          assessmentId,
          assessmentName: template.templateName,
          projectLogoURL,
          isLastAssessmentInStage: isLastAssessmentInStage,
          isAllowReasonableAdjustment: isAllowReasonableAdjustment,
          ...languageProps,
        },
      });

      return;
    }

    if (template.prongCode === 'SJT') {
      navigate('/situational-judgement-test', {
        state: {
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          assessmentId,
          assessmentName: template.templateName,
          projectLogoURL,
          isLastAssessmentInStage: isLastAssessmentInStage,
          isAllowReasonableAdjustment: isAllowReasonableAdjustment,
          ...languageProps,
        },
      });

      return;
    }

    if (
      template.prongCode === 'Numerical' ||
      template.prongCode === 'Verbal' ||
      template.prongCode === 'Abstract' ||
      template.prongCode === 'Mechanical' ||
      template.prongCode === 'Checking'
    ) {
      navigate(`/ability-test`, {
        state: {
          prongCode: template.prongCode,
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          assessmentId,
          assessmentName: template.templateName,
          projectLogoURL,
          isLastAssessmentInStage: isLastAssessmentInStage,
          isAllowReasonableAdjustment: isAllowReasonableAdjustment,
          ...languageProps,
        },
      });
      return;
    }

    if (template.prongCode === 'BlendedTest') {
      navigate(`/blended-test`, {
        state: {
          prongCode: template.prongCode,
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          assessmentId,
          assessmentName: template.templateName,
          projectLogoURL,
          isLastAssessmentInStage: isLastAssessmentInStage,
          isAllowReasonableAdjustment: isAllowReasonableAdjustment,
          ...languageProps,
        },
      });
      return;
    }

    if (template.prongCode === 'Classification') {
      navigate('/classification-test', {
        state: {
          prongCode: template.prongCode,
          assessmentProngID: template.assessmentProngID,
          jobApplicationId: jobApplicationId,
          projectId,
          assessmentId,
          assessmentName: template.templateName,
          projectLogoURL,
          isLastAssessmentInStage: isLastAssessmentInStage,
        },
      });

      return;
    }

    dispatch(
      setIFramedProng({
        iframeUrl: template.assessmentUrl,
        redirectUrl: `/view-application/${assessmentId}/${applicationType}/stages`,
        redirectUrlProps: redirectUrlProps,
        customClassName: `${
          template.prongCode ? `${template.prongCode}IFrame` : ''
        }`,
        isLastAssessmentInStage: isLastAssessmentInStage,
        jobApplicationId: jobApplicationId,
      })
    );

    navigate('/application-stage');
  };

  const getApplicationStageTemplates = (
    stageItems: WorkflowStageItem[],
    isDisabled: boolean
  ) => {
    // Check if this is the last item to be completed in the stage
    // a function to go through all stageItems to find the completed and not completed ones
    // Bookable templates are considered completed as advancing to the next stage works differently here
    // The complete stage endpoint can be called even if there are bookable stages remaining and it won't have a negative impact
    let completedTemplateIds: number[] = [];
    let uncompletedTemplateIds: number[] = [];

    if (Array.isArray(stageItems)) {
      stageItems.forEach((stageItem) => {
        if (stageItem.templates && Array.isArray(stageItem.templates)) {
          stageItem.templates.forEach((template) => {
            if (template.isCompleted) {
              completedTemplateIds.push(template.templateID);
            } else {
              uncompletedTemplateIds.push(template.templateID);
            }
          });
        }
      });
    }

    if (Array.isArray(stageItems)) {
      return stageItems
        .sort((a, b) => a.stageItemsOrderNumber - b.stageItemsOrderNumber)
        .map((stageItem, index, stageItems) => {
          if (stageItem.templates) {
            return stageItem.templates.map(
              (currentTemplate, index, templatesList) => {
                const template = currentTemplate;
                let isDisabledFinal = isDisabled;

                // Last template in the stage to be completed:
                let veryLastItem =
                  uncompletedTemplateIds.length === 1 &&
                  uncompletedTemplateIds[0] === currentTemplate.templateID;
                /// Reinstate below if clients changes mind on allowing access to every assessment in the current stage
                // const otherAssessments = stageItems.filter(
                //   (otherStageItem) =>
                //     !otherStageItem.manualActivities &&
                //     otherStageItem.stageItemsID !== stageItem.stageItemsID &&
                //     otherStageItem.stageItemsOrderNumber <
                //       stageItem.stageItemsOrderNumber
                // );

                // const isEarlierTemplateIncomplete = otherAssessments.some(
                //   (assessment) =>
                //     assessment &&
                //     assessment.templates &&
                //     Array.isArray(assessment.templates) &&
                //     !assessment.templates[0].isCompleted
                // );

                // if (
                //   !isDisabledFinal &&
                //   // isEarlierTemplateIncomplete &&
                //   !template.isBookable
                // ) {
                //   isDisabledFinal = true;
                // }
                ///

                const isCompleted =
                  template.isCompleted ||
                  applicationType === ApplicationSummaryType.HISTORIC;

                return (
                  <AssestmentCard
                    key={index}
                    index={template.templateID}
                    template={currentTemplate}
                    isDisabled={isDisabledFinal}
                    isCompleted={isCompleted}
                    handleStart={(template: WorkflowTemplate, language?: string) => handleStart(template, veryLastItem, language)}
                    cancelBooking={cancelBooking}
                    languageOptions={prongLanguageData && prongLanguageData[template.assessmentProngID]}
                  />
                );
              }
            );
          } else if (stageItem.manualActivities) {
            const manualActivity = stageItem.manualActivities[0];
            return (
              <div
                key={index}
                className={`ViewApplicationSummary ViewApplicationSummaryManual ${isDisabled ? 'Disabled' : ''
                  }`}
              >
                <div
                  className={`ViewApplicationStageTemplateInner ${isDisabled ? 'Disabled' : ''
                    }`}
                >
                  <div className="Progress ManualActivityProgress">
                    <div className="ProgressIndicatorContainer"></div>
                  </div>
                  <div className={`ViewApplicationTemplateDetails`}>
                    <div className={`ViewApplicationTemplateHeading`}>
                      <h3>{manualActivity.manualActivitiesTitle}</h3>
                      <span>
                        {t(
                          'SSCandidate_View_Application_Stages_View_Application_Application_Processed'
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        });
    }
  };

  const getRequirementsIcons = (
    fullRequirements: { requirement: string }[]
  ) => {
    const requirementIcons: { [key: string]: string } = {
      Desktop: DesktopLogo,
      Mobile: MobileLogo,
      TimeLimit: WatchLogo,
      Calculator: CalculatorLogo,
    };
    if (fullRequirements) {
      return fullRequirements.map(
        (requirementWrapper: { requirement: string }) => {
          return (
            <div className="RequirementIconContainer">
              <img
                src={requirementIcons[requirementWrapper.requirement]}
                alt={t(
                  'SSCandidate_View_Application_Stages_Alt_Requirement_Wrapper_Requirement_2'
                ) + requirementWrapper.requirement.toLowerCase() + "."}
              />
              <div className="TooltipBlock">
                {t(
                  'SSCandidate_View_Application_Stages_Alt_Requirement_Wrapper_Requirement_2'
                ) + requirementWrapper.requirement.toLowerCase() + "."}
              </div>
            </div>
          );
        }
      );
    }
    return null;
  };

  const getApplicationStages = (stages: WorkflowStage[]) => {
    return stages.map((stageOuter, index, stages) => {
      const haveCompletedPreviousStage =
        stages[index - 1] && stages[index - 1].stageItems
          ? stages[index - 1].stageItems.every((stageItem) => {
            if (stageItem.templates) {
              return stageItem.templates[0].isCompleted;
            } else return true;
          })
          : true;

      const isInitialManualActivity =
        stages &&
        Array.isArray(stages) &&
        stages.length === 1 &&
        stageOuter.orderNumber === 1 &&
        stageOuter.currentStageID === stageOuter.stageID &&
        (stageOuter.stageItems === null ||
          (stageOuter.stageItems &&
            Array.isArray(stageOuter.stageItems) &&
            stageOuter.stageItems[0].templates === null));

      const currentStageOrderNumber = stages.find(
        ({ currentStageID, stageID }) => currentStageID === stageID
      )?.orderNumber;

      const isLaterStage =
        typeof currentStageOrderNumber === 'number' &&
        stageOuter.orderNumber > currentStageOrderNumber;

      const isDisabled =
        applicationType === ApplicationSummaryType.HISTORIC || isLaterStage;

      // stageOuter.stageID > stageOuter.currentStageID;

      return (
        <>
          <div className="ViewApplicationStageTitleContainer">
            {stages.length > 1 && stageOuter.stageItems && (
              <div className="ViewApplicationStageTitleFirst">{`Stage ${stageOuter.orderNumber - (stages[0].stageItems ? 0 : 1)
                }`}</div>
            )}

            {isInitialManualActivity ||
              (stages[index - 1] &&
                currentStageOrderNumber &&
                stageOuter.orderNumber === currentStageOrderNumber + 1 &&
                !stages[index - 1].isApproved &&
                haveCompletedPreviousStage) ? (
              <div className="ViewApplicationStageWaiting">
                <span>
                  {t(
                    'SSCandidate_View_Application_Stages_View_Application_Waiting_For_Approval'
                  )}
                </span>
              </div>
            ) : (
              ''
            )}
          </div>
          {getApplicationStageTemplates(stageOuter.stageItems, isDisabled)}
        </>
      );
    });
  };

  const getBarClassName = (
    percentageComplete: number,
    isCompleted: boolean
  ) => {
    if (!isCompleted && percentageComplete >= 0 && percentageComplete < 100) {
      // return '#2A9A47';
      return '#036C8F';
    } else if (isCompleted || percentageComplete === 100) {
      // return '#f5f5f5';
      return '#2A9A47';
    } else {
      return '#f5f5f5';
    }
  };

  const checkApplicationCompletion = (stages: any) => {
    let applicationComplete = true;

    stages?.map((stage: any) => {
      stage.stageItems?.map((stageItem: any) => {
        stageItem.templates?.map((template: any) => {
          if (template.percentageCompletion !== 100) {
            applicationComplete = false;
          }
        });
      });
    });

    return applicationComplete;
  };

  useEffect(() => {
    if(title){
      const titleTag = document.getElementsByTagName("title")[0];
      titleTag.textContent = title;
  
      return () => {
        titleTag.textContent = "Find Your Joy";
      };
    }
  }, []);   

  if (viewSelected === ViewTypes.SCORES_SENT) {
    return (
      <div className={`ViewApplicationStages ScoresSent`}>
        <h2>{title}</h2>

        <div className="ApplicationDescTxt">
          <p>
            {t('SSCandidate_View_Application_Stages_Already_Sent_Scores_01')}
          </p>
          <p>
            {t('SSCandidate_View_Application_Stages_Already_Sent_Scores_02')}
          </p>
        </div>

        <button
          className="Btn BtnPrimary LrgBtn"
          onClick={() => {
            navigate(AppPath.ASSESSMENTS);
          }}
        >
          {t('SSCandidate_View_Application_Stages_View_Assessments')}
        </button>
        {redirectURL && (
          <button
            className="Btn BtnPrimary LrgBtn"
            onClick={() => {
              navigate(redirectURL);
            }}
          >
            {t('SSCandidate_View_Application_Stages_Redirect')}
          </button>
        )}
      </div>
    );
  } else if (viewSelected === ViewTypes.RESEND_SCORES) {
    return (
      <div className={`ViewApplicationStages ScoresSent`}>
        <h2>{title}</h2>

        <div className="ApplicationDescTxt">
          <p>{t('SSCandidate_View_Application_Stages_Resend_Scores_01')}</p>
          <p>{t('SSCandidate_View_Application_Stages_Resend_Scores_02')}</p>
        </div>

        <button
          className="Btn BtnPrimary LrgBtn"
          onClick={() => {
            setViewSelected(ViewTypes.DEFAULT);
          }}
        >
          {t('SSCandidate_View_Application_Stages_Return_To_Application')}
        </button>

        <button
          className="Btn BtnPrimary LrgBtn"
          onClick={() => {
            handleResendScores(projectId, applicationReference);
          }}
        >
          {t('SSCandidate_View_Application_Stages_Resend_Scores')}
        </button>
      </div>
    );
  } else if (viewSelected === ViewTypes.REDIRECT_PAGE) {
    return (
      <RedirectCustomPage
        redirectPageText={redirectText}
        redirectPageUrl={redirectURL}
      />
    );
  }

  return (
    <div
      className={`ViewApplicationStages ViewApplicationLeft ${currentWebskin !== 'defaultTheme' ? 'Webskin' : ''
        }`}
    >
      <div className="ViewApplicationTitleContainer">
        <h1>{title}</h1>
        {stages && !checkApplicationCompletion(stages) && (
          <div className="ButtonContainer">
            <Button
              className="SideMenuBtn Rounded AccessibilityBtn"
              priority={'tertiary'}
              disabled={false}
              addTabIndex={0}
              onClick={() => {
                dispatch(
                  openModal({
                    modalType: 'ASSESSMENT_PRACTICE_DASHBOARD_ACCESSIBILITY',
                    data: {
                      title: 'Accessibility settings',
                      modalHeaderClasses: 'VirtualAssessment',
                    },
                  })
                );
              }}
            >
              Accessibility
            </Button>

            {currentWebskin === 'SSCL-MoJ' &&
              <button
                className="Btn BtnTertiary SkipContentBtn"
                onClick={() => {
                  const assessmentFirstRow = document.getElementsByClassName("ViewApplicationSummary")[0];
                  if (assessmentFirstRow) {
                    const assessmentFirstBtn = assessmentFirstRow.getElementsByTagName("button")[0];
                    if (assessmentFirstBtn) {
                      assessmentFirstBtn.focus();
                    }
                  }
                }}
              >
                Skip To Main Content
              </button>            
            }

            {isAssessmentOnly && (
              <Button
                priority={'tertiary'}
                onClick={() => {
                  navigate(
                    `/view-application/${assessmentId}/${applicationType}/help`,
                    {
                      state: { projectId },
                    }
                  );
                }}
                fontSize="medium"
                roundedRectangle
              >
                {t('SSCandidate_View_Application_Stages_Help')}
              </Button>
            )}

            <button
              className="IconGuideContainer"
              onClick={() => {
                dispatch(
                  openModal({
                    modalType: 'ICON_GUIDE',
                    data: {
                      title: t(
                        'SSCandidate_View_Application_Stages_Icon_Guide'
                      ),
                      modalClasses: 'IconGuideModalMain',
                      modalHeaderClasses: 'IconGuideModalHeader',
                    },
                  })
                );
              }}
            >
              <img
                src={
                  AccessibilitySwitcher(
                    BigInformationLogo, 
                    BigInformationLogoYellow, 
                    BigInformationLogoWhite, 
                    BigInformationLogoBlack, 
                    accessibilityData
                  )
                }
                alt=""
              ></img>
              {windowDimensions.width > 1024 && (
                <span>
                  {t('SSCandidate_View_Application_Stages_Icon_Guide')}
                </span>
              )}
            </button>
          </div>
        )}
      </div>

      {isAssessmentOnly && (
        <p>
          <a className="BrowserCheckerLink" href={`/assessments-browser-checker-redirect/${assessmentId}/${applicationType}/${projectId}`}>
            {t('SSCandidate_View_Application_Stages_Browser_Compatibility_01')}
          </a>{' '}
          {t('SSCandidate_View_Application_Stages_Browser_Compatibility_02')}
        </p>
      )}

      {campaignWelcomeText && <p>{campaignWelcomeText}</p>}

      {stages && checkApplicationCompletion(stages) && (
        <div className="ApplicationDescTxt">
          <p>{t('SSCandidate_View_Application_Stages_Pending_Complete_01')}</p>
          {currentWebskin === "MandS" ?
            <p>{t('SSCandidate_View_Application_Stages_MandS_In_Touch')}</p>
            :
            <p>
              {t('SSCandidate_View_Application_Stages_Pending_Complete_02')}{' '}
              {clientName && (
                <>
                  {t('SSCandidate_View_Application_Stages_Pending_Complete_03')}{' '}
                  {clientName}
                  {t('SSCandidate_View_Application_Stages_Pending_Complete_04')}
                </>
              )}
            </p>
          }
        </div>
      )}

      {stages && getApplicationStages(stages)}

      {stages && checkApplicationCompletion(stages) && isAssessmentOnly && (
        <>
          {webSkinDetails.isShowJoyTextAtEndOfAssessment ? (
            <div
              className="ApplicationDescTxt"
              dangerouslySetInnerHTML={{
                __html: t(
                  'SSCandidate_View_Application_Stages_Pending_Complete_05'
                ),
              }}
            />
          ) : (
            <div className='ApplicationDescTxt'><p>{""}</p></div>
          )}
          <button
            className={`Btn CloseApplicationBtn ${currentWebskin === 'defaultTheme' ? 'BtnPrimary' : 'BtnSecondary'
              } LrgBtn`}
            onClick={() => dispatch(signOut())}
          >
            {t('SSCandidate_View_Application_Stages_Close')}
          </button>
        </>
      )}
    </div>
  );
};

export default ViewApplicationStages;
